import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Target, BarChart3, Tag, AlertTriangle, Lightbulb, Scale, TrendingUp, Radio, Zap } from 'lucide-react';
import { apiClient, RegimeBacktestResponse } from '../../api/client';
import { formatFinancialPrice } from '../../shared/domain/formatters';
import { compareDecimalStrings, formatDecimalPercent } from '../../shared/domain/decimal';
import { Lifecycle5State } from '../../components/common/QuantitativeDataTable';
import { CryptoTickerBar } from '../../components/common';
import { CANONICAL_SPOT_SYMBOLS, VENUE_METADATA, CanonicalSpotSymbol } from '../../shared/constants/watchlist';
import { TagAttributionLabTab } from './TagAttributionLabTab';
import { MultiTimeframeBenchmarkTab } from './MultiTimeframeBenchmarkTab';
import { CRYPTO_BILINGUAL } from '../../shared/constants/bilingualTitles';

interface DistributionBin {
  bin_index: number;
  price_low: string;
  price_high: string;
  price_center: string;
  volume: string;
  volume_pct: string;
  is_poc: boolean;
  is_inside_va: boolean;
}

interface VolumeProfileCoreData {
  current_close: string;
  profile_high: string;
  profile_low: string;
  total_volume: string;
  poc_price: string;
  poc_volume: string;
  vah_price: string;
  val_price: string;
  va_volume: string;
  va_volume_ratio: string;
  position_relative_to_va: string;
  distance_to_poc_pct: string;
  distance_to_vah_pct: string;
  distance_to_val_pct: string;
  price_step: string;
}

interface VolumeProfileResponse {
  symbol: string;
  timeframe: string;
  lookback: number;
  bins: number;
  as_of_time_utc: string;
  watermark_lag_seconds: string;
  status: Lifecycle5State;
  data: VolumeProfileCoreData | null;
  distribution: DistributionBin[];
}

interface TrendWeatherSymbolEntry {
  symbol: string;
  unified_mark_price: string;
  regime_state?: string;
}

interface TrendWeatherMatrixResponse {
  schema_version: string;
  generated_at: string;
  bar_close_utc: string;
  symbols: TrendWeatherSymbolEntry[];
}

type SortField = 'symbol' | 'price' | 'poc_dist' | 'win_rate' | 'profit_factor';
type SortDirection = 'asc' | 'desc';

export const RegimeDecisionBacktestView: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('1h');
  const [profiles, setProfiles] = useState<Record<string, VolumeProfileResponse>>({});
  const [backtests, setBacktests] = useState<Record<string, RegimeBacktestResponse | null>>({});
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [state, setState] = useState<Lifecycle5State>('loading');
  const [lastUpdatedUtc, setLastUpdatedUtc] = useState<string>('');
  const [modalSymbol, setModalSymbol] = useState<CanonicalSpotSymbol | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [modalTab, setModalTab] = useState<'backtest' | 'profile' | 'attribution'>('backtest');
  const [mainViewTab, setMainViewTab] = useState<'matrix' | 'lab' | 'confluence'>('matrix');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const fetchAllData = useCallback(async () => {
    setState('loading');
    try {
      // 1. Fetch Trend Weather Matrix for authoritative mark prices
      const markPrices: Record<string, string> = {};
      try {
        const matrixRes = (await apiClient.getTrendWeatherMatrix()) as unknown as TrendWeatherMatrixResponse;
        if (matrixRes && matrixRes.symbols) {
          for (const s of matrixRes.symbols) {
            markPrices[s.symbol] = s.unified_mark_price;
          }
          setPrices(markPrices);
        }
      } catch (e) {
        console.warn('Matrix fetch error, fallback to profile close prices:', e);
      }

      // 2. Fetch Volume Profile for all 7 symbols in parallel
      const profilePromises = CANONICAL_SPOT_SYMBOLS.map(async (sym) => {
        try {
          const res = await apiClient.get<VolumeProfileResponse>(
            `/api/v1/indicators/volume-profile?symbol=${sym}&timeframe=${selectedTimeframe}&lookback=50&bins=25`
          );
          return { symbol: sym, data: res };
        } catch (err) {
          console.warn(`Volume profile fetch failed for ${sym}:`, err);
          return { symbol: sym, data: null };
        }
      });

      // 3. Fetch Streaming Regime Backtest for all 7 symbols in parallel
      // Truthful error handling: return null on rejection rather than fabricating mock data
      const backtestPromises = CANONICAL_SPOT_SYMBOLS.map(async (sym) => {
        try {
          const res = await apiClient.getRegimeBacktest(sym, selectedTimeframe);
          return { symbol: sym, data: res };
        } catch (err) {
          console.warn(`Regime backtest fetch failed for ${sym}:`, err);
          return { symbol: sym, data: null };
        }
      });

      const [profileResults, backtestResults] = await Promise.all([
        Promise.allSettled(profilePromises),
        Promise.allSettled(backtestPromises),
      ]);

      const newProfiles: Record<string, VolumeProfileResponse> = {};
      for (const r of profileResults) {
        if (r.status === 'fulfilled' && r.value.data) {
          newProfiles[r.value.symbol] = r.value.data;
        }
      }

      const newBacktests: Record<string, RegimeBacktestResponse | null> = {};
      for (const r of backtestResults) {
        if (r.status === 'fulfilled') {
          newBacktests[r.value.symbol] = r.value.data;
        }
      }

      setProfiles(newProfiles);
      setBacktests(newBacktests);

      // Derive global backtest panel state truthfully from backtest responses (G30-053B-04)
      const backtestList = Object.values(newBacktests);
      const validBacktests = backtestList.filter((b): b is RegimeBacktestResponse => b !== null);
      if (validBacktests.length === 0) {
        setState('disconnected');
      } else {
        const statuses = backtestList.map((b) => (b ? b.status : 'disconnected'));
        if (statuses.some((s) => s === 'fresh')) {
          setState('fresh');
        } else if (statuses.some((s) => s === 'stale')) {
          setState('stale');
        } else if (statuses.every((s) => s === 'disconnected')) {
          setState('disconnected');
        } else if (statuses.every((s) => s === 'empty')) {
          setState('empty');
        } else if (statuses.some((s) => s === 'disconnected')) {
          setState('disconnected');
        } else {
          setState('empty');
        }
      }

      // Truthful update timestamp: only retain real non-null timestamps from successful evaluations (G30-053B-03)
      const validTimestamps = validBacktests
        .map((b) => b.last_successful_update_utc)
        .filter((t): t is string => Boolean(t));
      if (validTimestamps.length > 0) {
        validTimestamps.sort();
        setLastUpdatedUtc(validTimestamps[validTimestamps.length - 1]);
      } else {
        setLastUpdatedUtc('');
      }
    } catch (err) {
      console.error('Failed to fetch regime decision data:', err);
      setState('disconnected');
      setLastUpdatedUtc('');
    }
  }, [selectedTimeframe]);

  useEffect(() => {
    fetchAllData();
    const timer = setInterval(() => {
      fetchAllData();
    }, 30000);
    return () => clearInterval(timer);
  }, [fetchAllData]);

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Filtered and sorted symbols: enforces Pure Decimal string comparison (G30-053B-01)
  const displayedSymbols = useMemo(() => {
    let list = [...CANONICAL_SPOT_SYMBOLS];
    if (searchFilter.trim()) {
      const q = searchFilter.trim().toUpperCase();
      list = list.filter((s) => s.includes(q));
    }

    if (sortField) {
      list.sort((a, b) => {
        if (sortField === 'symbol') {
          return sortDir === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
        }
        if (sortField === 'price') {
          const valA = prices[a] || profiles[a]?.data?.current_close || '0';
          const valB = prices[b] || profiles[b]?.data?.current_close || '0';
          const cmp = compareDecimalStrings(valA, valB);
          return sortDir === 'asc' ? cmp : -cmp;
        } else if (sortField === 'poc_dist') {
          const valA = (profiles[a]?.data?.distance_to_poc_pct || '0').replace('-', '');
          const valB = (profiles[b]?.data?.distance_to_poc_pct || '0').replace('-', '');
          const cmp = compareDecimalStrings(valA, valB);
          return sortDir === 'asc' ? cmp : -cmp;
        } else if (sortField === 'win_rate') {
          const valA = backtests[a]?.metrics?.win_rate_descriptive || '0';
          const valB = backtests[b]?.metrics?.win_rate_descriptive || '0';
          const cmp = compareDecimalStrings(valA, valB);
          return sortDir === 'asc' ? cmp : -cmp;
        } else if (sortField === 'profit_factor') {
          const valA = backtests[a]?.metrics?.gross_profit_factor || '0';
          const valB = backtests[b]?.metrics?.gross_profit_factor || '0';
          const cmp = compareDecimalStrings(valA, valB);
          return sortDir === 'asc' ? cmp : -cmp;
        }
        return 0;
      });
    }

    return list;
  }, [searchFilter, sortField, sortDir, prices, profiles, backtests]);

  // Selected Data for Modal
  const modalProfile = modalSymbol ? profiles[modalSymbol] : null;
  const modalBacktest = modalSymbol ? backtests[modalSymbol] : null;
  const modalMeta = modalSymbol ? VENUE_METADATA[modalSymbol] : null;

  return (
    <div className="view-panel active" id="view-regime-decision" role="tabpanel">
      {/* 1. Standardized TSO 2.0 View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Target size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{CRYPTO_BILINGUAL.regime.zh}</h2>
            <span
              className="badge"
              style={{
                fontSize: '10px',
                marginLeft: '4px',
                background: 'var(--badge-info-bg)',
                color: 'var(--accent-cyan)',
                border: '1px solid var(--badge-info-border)',
              }}
            >
              TSO 2.0 纯 Decimal
            </span>
          </div>
          <span className="bilingual-header-subtitle-en">{CRYPTO_BILINGUAL.regime.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            多标的横向决策表 · 微观 1m 全时序穿透 · 筹码核心带 (POC/VAH/VAL) · 实时流式因果回测观测 (W ≥ 3.000s)
          </p>
        </div>

        {/* Header Controls & Actions */}
        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Search box with clear button */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="搜索标的..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="regime-search-input"
              style={{ width: '130px', height: '28px', fontSize: '11px' }}
            />
            {searchFilter && (
              <button
                onClick={() => setSearchFilter('')}
                style={{
                  position: 'absolute',
                  right: '6px',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  fontSize: '11px',
                  padding: '2px',
                }}
                title="清除搜索"
              >
                ✕
              </button>
            )}
          </div>

          {/* Timeframe Selector Pill */}
          <div className="market-type-toggle">
            {(['5m', '15m', '30m', '1h', '4h', '1d'] as const).map((tf) => {
              const active = selectedTimeframe === tf;
              return (
                <button
                  key={tf}
                  onClick={() => setSelectedTimeframe(tf)}
                  className={`toggle-btn ${active ? 'active' : ''}`}
                >
                  {tf}
                </button>
              );
            })}
          </div>

          {/* Telemetry Status Badge */}
          <div
            title={lastUpdatedUtc ? `最新成功评估: ${lastUpdatedUtc}` : '暂无成功回测更新'}
            className={`badge ${
              state === 'fresh'
                ? 'badge-bullish'
                : state === 'stale'
                ? 'badge-warning'
                : state === 'disconnected'
                ? 'badge-bearish'
                : 'badge-neutral'
            }`}
            style={{ fontSize: '10px', height: '24px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            <span>●</span>
            <span>{state.toUpperCase()}</span>
            {lastUpdatedUtc && (
              <span style={{ fontSize: '9px', opacity: 0.85, fontFamily: 'var(--font-mono)' }}>
                {lastUpdatedUtc.slice(11, 19)}Z
              </span>
            )}
          </div>

          {/* Refresh Action Button */}
          <button
            className="toggle-btn"
            id="btn-refresh-regime-decision"
            onClick={fetchAllData}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              padding: '4px 10px',
              height: '26px',
              fontSize: '11px',
            }}
            title="手动刷新全局量化决策表"
          >
            ↻ 刷新
          </button>
        </div>
      </div>

      {/* View Sub-Navigation Tabs */}
      <div style={{ display: 'flex', gap: '8px', margin: '4px 0 10px 0' }}>
        <button
          id="btn-subnav-matrix"
          onClick={() => setMainViewTab('matrix')}
          className={`toggle-btn bilingual-tab-btn ${mainViewTab === 'matrix' ? 'active' : ''}`}
        >
          <div className="bilingual-tab-title-zh">
            <BarChart3 size={13} />
            <span>多标的态势与回测矩阵</span>
          </div>
          <span className="bilingual-tab-subtitle-en">DECISION MATRIX</span>
        </button>
        <button
          id="btn-subnav-attribution-lab"
          onClick={() => setMainViewTab('lab')}
          className={`toggle-btn bilingual-tab-btn ${mainViewTab === 'lab' ? 'active' : ''}`}
        >
          <div className="bilingual-tab-title-zh">
            <Tag size={13} />
            <span>因子归因实验室</span>
          </div>
          <span className="bilingual-tab-subtitle-en">TAG ATTRIBUTION LAB</span>
        </button>
        <button
          id="btn-subnav-confluence-benchmark"
          onClick={() => setMainViewTab('confluence')}
          className={`toggle-btn bilingual-tab-btn ${mainViewTab === 'confluence' ? 'active' : ''}`}
        >
          <div className="bilingual-tab-title-zh">
            <Zap size={13} />
            <span>多周期共振与 AI Benchmark</span>
          </div>
          <span className="bilingual-tab-subtitle-en">MTF & AI BENCHMARK</span>
        </button>
      </div>

      {mainViewTab === 'confluence' && (
        <MultiTimeframeBenchmarkTab />
      )}

      {mainViewTab === 'lab' && (
        <TagAttributionLabTab
          initialSymbol="BTCUSDT"
          initialTimeframe={selectedTimeframe}
        />
      )}

      {mainViewTab === 'matrix' && (
        <>
          {/* Disconnected / Empty Notice Banners */}
          {state === 'disconnected' && (
            <div
              style={{
                padding: '8px 14px',
                borderRadius: '6px',
                background: 'var(--alert-bearish-bg)',
                border: '1px solid var(--alert-bearish-border)',
                color: 'var(--color-rose)',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px',
              }}
            >
              <AlertTriangle size={14} />
              <span>后端流式回测服务未就绪或时序连接中断 (DISCONNECTED) · 请检查底层 TimescaleDB 服务或 API 网关</span>
            </div>
          )}

      {/* 2. Top Summary KPI Strip: Live Observability Telemetry */}
      <div className="indicator-resonance-card" id="regime-summary-strip">
        <div className="ind-stat-item">
          <span className="ind-stat-label">全局回测状态</span>
          <div className="ind-stat-val">
            <span
              className={`badge ${
                state === 'fresh'
                  ? 'badge-bullish'
                  : state === 'stale'
                  ? 'badge-warning'
                  : state === 'disconnected'
                  ? 'badge-bearish'
                  : 'badge-neutral'
              }`}
              style={{ fontSize: '10px', padding: '1px 6px' }}
            >
              ● {state.toUpperCase()}
            </span>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              {lastUpdatedUtc ? `${lastUpdatedUtc.slice(11, 19)}Z` : '诚实零伪造'}
            </span>
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">微观时序穿透</span>
          <div className="ind-stat-val">
            <span style={{ color: 'var(--color-bullish)', fontWeight: 700 }}>6 周期全穿透</span>
            <span className="strip-pill-badge" style={{ background: 'var(--badge-bullish-bg)', color: 'var(--color-bullish)', border: '1px solid var(--badge-bullish-border)' }}>
              NO FALLBACK
            </span>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>1m 全量遍历 · 缺条熔断</span>
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">因果时延守卫</span>
          <div className="ind-stat-val">
            <span style={{ color: 'var(--color-bullish)', fontWeight: 700 }}>W ≥ 3.000s</span>
            <span className="strip-pill-badge" style={{ background: 'var(--badge-info-bg)', color: 'var(--accent-cyan)', border: '1px solid var(--badge-info-border)' }}>
              GATE-CAUSAL
            </span>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>次根开盘入场 · 严格防偷价</span>
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">刚性交易摩擦</span>
          <div className="ind-stat-val">
            <span style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>-14.0 bps</span>
            <span className="strip-pill-badge" style={{ background: 'var(--badge-neutral-bg)', color: 'var(--text-secondary)', border: '1px solid var(--badge-neutral-border)' }}>
              PURE DECIMAL
            </span>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>双边 5bps 费率 + 2bps 滑点</span>
          </div>
        </div>
      </div>

      {/* 3. Main Quantitative Decision Matrix Table (All 7 Assets) */}
      <section className="regime-table-card">
        {/* Table Header Bar */}
        <div className="regime-table-header-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BarChart3 size={15} style={{ color: 'var(--accent-cyan)' }} />
            <div className="bilingual-card-title">
              <span className="bilingual-card-title-zh" style={{ fontWeight: 700 }}>
                多标的量化决策与形态回测矩阵
              </span>
              <span className="bilingual-card-subtitle-en">MULTI-ASSET QUANTITATIVE DECISION MATRIX</span>
            </div>
            <span
              style={{
                fontSize: '10px',
                padding: '1px 6px',
                borderRadius: '4px',
                background: 'var(--bg-canvas)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              共 {displayedSymbols.length} / {CANONICAL_SPOT_SYMBOLS.length} 标的
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
              ↔ 横向平滑滚动查看完整字段 (首列标的已锁定)
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Lightbulb size={12} style={{ color: 'var(--accent-cyan)' }} />
              <span>点击任意行查看筹码分布与回测明细</span>
            </span>
          </div>
        </div>

        {/* Scrollable Table Area */}
        <div className="regime-table-scroll-wrapper">
          <table className="regime-decision-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('symbol')}
                  className="sortable regime-sticky-symbol-th"
                  style={{ minWidth: '95px', width: '95px' }}
                >
                  标的 / 现价 {sortField === 'symbol' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th
                  onClick={() => handleSort('poc_dist')}
                  className="sortable"
                  style={{ minWidth: '260px' }}
                >
                  筹码核心带 (POC & 70% 价值区) {sortField === 'poc_dist' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th
                  onClick={() => handleSort('win_rate')}
                  className="sortable"
                  style={{ minWidth: '300px' }}
                >
                  描述性回测指标 (样本数 / 胜率 / 获利因子) {sortField === 'win_rate' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th style={{ minWidth: '160px' }}>回测数据与时延状态</th>
                <th style={{ minWidth: '130px', textAlign: 'center' }}>操作与检验</th>
              </tr>
            </thead>
            <tbody>
              {displayedSymbols.map((sym) => {
                const prof = profiles[sym];
                const backtest = backtests[sym];
                const price = prices[sym] || prof?.data?.current_close || '—';
                const pStatus = prof?.status || 'loading';
                const bStatus = backtest?.status || 'loading';
                const meta = VENUE_METADATA[sym as CanonicalSpotSymbol];

                const posLabel =
                  prof?.data?.position_relative_to_va === 'INSIDE_VALUE_AREA'
                    ? '区内吸筹'
                    : prof?.data?.position_relative_to_va === 'ABOVE_VAH'
                    ? '突破向上'
                    : prof?.data?.position_relative_to_va === 'BELOW_VAL'
                    ? '折价吸纳'
                    : '计算中';

                return (
                  <tr
                    key={sym}
                    onClick={() => setModalSymbol(sym as CanonicalSpotSymbol)}
                    title={`点击查看 ${sym} 完整筹码分布图与回测检验明细`}
                  >
                    {/* 1. Asset & Price (Sticky First Column) - Clean vertical layout without venue pill */}
                    <td className="regime-sticky-symbol-td">
                      <div
                        className="symbol-cell"
                        style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}
                        title={meta ? `流动性主市场: ${meta.venue} (${meta.role})` : undefined}
                      >
                        <span className="symbol-name">
                          {sym.replace('USDT', '')}
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500 }}>/USDT</span>
                        </span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '12px', color: 'var(--accent-cyan)' }}>
                          {price !== '—' ? formatFinancialPrice(price, sym) : '—'}
                        </span>
                      </div>
                    </td>

                    {/* 2. Volume Profile Core Band */}
                    <td>
                      {prof?.data ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--color-rose)', fontWeight: 700 }}>
                              ★ POC:
                            </span>
                            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--color-rose)', fontSize: '12px' }}>
                              {formatFinancialPrice(prof.data.poc_price, sym)}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                              ({prof.data.distance_to_poc_pct}%)
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px', fontFamily: 'var(--font-mono)' }}>
                            VA 70%: [{formatFinancialPrice(prof.data.val_price, sym)} ~ {formatFinancialPrice(prof.data.vah_price, sym)}]
                            <span
                              style={{
                                marginLeft: '6px',
                                padding: '1px 5px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                background: 'var(--badge-info-bg)',
                                color: 'var(--accent-cyan)',
                                fontWeight: 600,
                              }}
                            >
                              {posLabel}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ opacity: 0.6 }}>⚡</span>
                          <span>{pStatus === 'loading' ? '计算筹码分布中...' : '等待时序闭合中'}</span>
                        </div>
                      )}
                    </td>

                    {/* 3. Backtest Effectiveness (Dynamic Live Observability, Zero Mock, Pure Decimal) */}
                    <td>
                      {bStatus === 'fresh' && backtest?.metrics ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>观测状态</div>
                            <div style={{ fontWeight: 800, fontSize: '11px', color: 'var(--color-bullish)', fontFamily: 'var(--font-mono)' }}>
                              LIVE_OBSERVATIONAL
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                              样本 {backtest.metrics.completed_sample_count} / {backtest.required_samples}
                            </div>
                          </div>
                          <div style={{ borderLeft: '1px solid var(--border-subtle)', paddingLeft: '8px' }}>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>滚动胜率</div>
                            <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                              {formatDecimalPercent(backtest.metrics.win_rate_descriptive)}
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--color-bullish)' }}>
                              因果闭合 1m
                            </div>
                          </div>
                          <div style={{ borderLeft: '1px solid var(--border-subtle)', paddingLeft: '8px' }}>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>获利因子 / 均利</div>
                            <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
                              {backtest.metrics.gross_profit_factor ? `${backtest.metrics.gross_profit_factor}` : 'N/A'} / {backtest.metrics.net_return_average_bps}bp
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                              扣除 14.0bp 摩擦
                            </div>
                          </div>
                        </div>
                      ) : bStatus === 'stale' && backtest?.metrics ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>观测状态</div>
                            <div style={{ fontWeight: 800, fontSize: '11px', color: 'var(--color-warning)', fontFamily: 'var(--font-mono)' }}>
                              STALE (时滞)
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                              样本 {backtest.metrics.completed_sample_count} / {backtest.required_samples}
                            </div>
                          </div>
                          <div style={{ borderLeft: '1px solid var(--border-subtle)', paddingLeft: '8px' }}>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>滚动胜率</div>
                            <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                              —
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--color-warning)' }}>
                              水印滞后 (指标抑制)
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>系统状态</div>
                            <div
                              style={{
                                fontWeight: 800,
                                fontSize: '11px',
                                color:
                                  bStatus === 'disconnected'
                                    ? 'var(--color-rose)'
                                    : bStatus === 'empty'
                                    ? 'var(--color-warning)'
                                    : 'var(--text-muted)',
                                fontFamily: 'var(--font-mono)',
                              }}
                            >
                              {bStatus.toUpperCase()}
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                              {backtest?.reason_code || (bStatus === 'loading' ? '正在连接时序服务' : bStatus === 'disconnected' ? '时序服务连接中断' : '等待时序数据')}
                            </div>
                          </div>
                          <div style={{ borderLeft: '1px solid var(--border-subtle)', paddingLeft: '8px' }}>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>已结算样本</div>
                            <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                              {backtest?.sample_count !== undefined ? `${backtest.sample_count} / ${backtest.required_samples}` : '—'}
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--color-warning)' }}>
                              诚实零伪造
                            </div>
                          </div>
                          <div style={{ borderLeft: '1px solid var(--border-subtle)', paddingLeft: '8px' }}>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>因果穿透</div>
                            <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                              1m 全序列
                            </div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                              W &ge; 3.0s
                            </div>
                          </div>
                        </div>
                      )}
                    </td>

                    {/* 4. Backtest Data Status (Truthful 5-State Machine) */}
                    <td>
                      <span
                        className={`badge ${
                          bStatus === 'fresh'
                            ? 'badge-bullish'
                            : bStatus === 'stale'
                            ? 'badge-warning'
                            : bStatus === 'disconnected'
                            ? 'badge-bearish'
                            : 'badge-neutral'
                        }`}
                        style={{ fontSize: '10px', padding: '1px 6px' }}
                      >
                        ● {bStatus.toUpperCase()}
                      </span>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', fontFamily: 'var(--font-mono)' }}>
                        {backtest?.data_watermark_utc ? `水印: ${backtest.data_watermark_utc.slice(11, 19)}Z` : (bStatus === 'disconnected' ? '服务离线或中断' : '时序接入中')} · W &ge; 3.0s
                      </div>
                    </td>

                    {/* 5. Action */}
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setModalSymbol(sym as CanonicalSpotSymbol);
                        }}
                        className="toggle-btn"
                        style={{
                          padding: '3px 10px',
                          fontSize: '11px',
                          color: 'var(--accent-cyan)',
                          borderColor: 'var(--badge-info-border)',
                          background: 'var(--bg-canvas)',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                        title="查看深度筹码分布与回测明细"
                      >
                        🔍 查看明细
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* 4. Strategy Governance & Boundary Card */}
      <footer
        style={{
          marginTop: '10px',
          padding: '10px 14px',
          borderRadius: '6px',
          background: 'var(--badge-warning-bg)',
          border: '1px solid var(--badge-warning-border)',
          fontSize: '11px',
          color: 'var(--text-secondary)',
          lineHeight: '1.5',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Scale size={13} style={{ color: 'var(--color-warning)' }} />
          <strong style={{ color: 'var(--color-warning)' }}>严格只读免责声明：</strong>
        </span>
        本视图展示的流式回测指标与 POC/VAH/VAL 筹码价值区核心带，严格用于验证 Market-Hub 行情数据时序回测体系的有效性与可观测性。Market-Hub 严格保持为只读分析数据中心，<strong>绝不提供任何实盘交易策略指导、执行指令或下单服务</strong>。
      </footer>
        </>
      )}

      {/* 5. Detail Modal Popup (Tabbed & Polished Dialog) */}
      {modalSymbol && modalMeta && (
        <div
          className="regime-modal-overlay"
          onClick={() => setModalSymbol(null)}
        >
          <div
            className="regime-modal-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="regime-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <BarChart3 size={24} style={{ color: 'var(--accent-cyan)' }} />
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)' }}>
                      {modalSymbol.replace('USDT', '')} / USDT · 量化态势与回测明细
                    </h2>
                    <span
                      style={{
                        fontSize: '10px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: 'var(--badge-info-bg)',
                        border: '1px solid var(--badge-info-border)',
                        color: 'var(--accent-cyan)',
                        fontWeight: 700,
                      }}
                    >
                      {modalMeta.venue} · {modalMeta.role}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>
                    周期: {selectedTimeframe} · 纯 Decimal 因果水印 (W &ge; 3.000s)
                  </div>
                </div>
              </div>

              <button
                onClick={() => setModalSymbol(null)}
                style={{
                  background: 'var(--bg-canvas)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  transition: 'all 0.15s ease',
                }}
              >
                ✕
              </button>
            </div>

            {/* Fast Symbol Switcher inside Modal */}
            <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
              <CryptoTickerBar
                id="modal-crypto-ticker-bar"
                compact
                selectedSymbol={modalSymbol}
                onSelectSymbol={(sym) => setModalSymbol(sym)}
              />
            </div>

            {/* Modal Navigation Tabs */}
            <nav className="regime-modal-tabs">
              <button
                onClick={() => setModalTab('backtest')}
                className={`regime-modal-tab-btn bilingual-tab-btn ${modalTab === 'backtest' ? 'active' : ''}`}
                style={{ padding: '4px 12px', minHeight: '40px' }}
              >
                <div className="bilingual-tab-title-zh">
                  <Radio size={12} />
                  <span>流式因果回测观测</span>
                </div>
                <span className="bilingual-tab-subtitle-en">STAGE 053B TELEMETRY</span>
              </button>
              <button
                onClick={() => setModalTab('profile')}
                className={`regime-modal-tab-btn bilingual-tab-btn ${modalTab === 'profile' ? 'active' : ''}`}
                style={{ padding: '4px 12px', minHeight: '40px' }}
              >
                <div className="bilingual-tab-title-zh">
                  <TrendingUp size={12} />
                  <span>筹码价量分布</span>
                </div>
                <span className="bilingual-tab-subtitle-en">VOLUME PROFILE & POC</span>
              </button>
              <button
                onClick={() => setModalTab('attribution')}
                className={`regime-modal-tab-btn bilingual-tab-btn ${modalTab === 'attribution' ? 'active' : ''}`}
                style={{ padding: '4px 12px', minHeight: '40px' }}
              >
                <div className="bilingual-tab-title-zh">
                  <Tag size={12} />
                  <span>因子归因实验室</span>
                </div>
                <span className="bilingual-tab-subtitle-en">TAG ATTRIBUTION LAB</span>
              </button>
            </nav>

            {/* Modal Body */}
            <div className="regime-modal-body">
              {/* TAB 1: Streaming Regime Backtest Observability (Stage 053B) */}
              {modalTab === 'backtest' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {/* Live Observability Telemetry Banner */}
                  <div
                    style={{
                      padding: '14px 16px',
                      borderRadius: '10px',
                      background: 'var(--badge-info-bg)',
                      border: '1px solid var(--badge-info-border)',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.6',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Radio size={14} style={{ color: 'var(--accent-cyan)' }} />
                        <span style={{ fontWeight: 800, color: 'var(--accent-cyan)', fontSize: '13px' }}>
                          Stage 053B 实时流式因果回测观测中枢
                        </span>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 700,
                            fontFamily: 'var(--font-mono)',
                            background:
                              modalBacktest?.status === 'fresh'
                                ? 'var(--badge-bullish-bg)'
                                : modalBacktest?.status === 'stale'
                                ? 'var(--badge-warning-bg)'
                                : modalBacktest?.status === 'disconnected'
                                ? 'var(--badge-bearish-bg)'
                                : 'var(--badge-neutral-bg)',
                            color:
                              modalBacktest?.status === 'fresh'
                                ? 'var(--color-bullish)'
                                : modalBacktest?.status === 'stale'
                                ? 'var(--color-warning)'
                                : modalBacktest?.status === 'disconnected'
                                ? 'var(--color-rose)'
                                : 'var(--text-secondary)',
                          }}
                        >
                          {modalBacktest?.status?.toUpperCase() || (modalBacktest === null ? 'DISCONNECTED' : 'EMPTY')}
                        </span>
                      </div>
                      <span style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                        原因码: {modalBacktest?.reason_code || (modalBacktest === null ? 'CLIENT_FETCH_ERROR_OR_SERVICE_DISCONNECTED' : 'INSUFFICIENT_SAMPLES')} · 水印: {modalBacktest?.data_watermark_utc || '—'}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-secondary)' }}>
                      • <strong>微观全 1m 时序穿透</strong>：所有周期信号严格自底层 1m 闭合烛线遍历，坚决拒绝向低分辨率降级。<br />
                      • <strong>因果闭合守卫 ($W \ge 3s$)</strong>：次根 Bar 开盘点因果准入，SL-First 优先止损，剔除偷价可能。<br />
                      • <strong>扣除 14.0 bps 刚性摩擦</strong>：单点扣除 5bps 费率 + 2bps 滑点双边合计，展现扣费后真实净收益。<br />
                      • <strong>去官僚化纯观测模式</strong>：纯描述性统计，移除易过拟合的假 p 值与单点 100% 胜率宣称，以诚实 5 态流转展现真实样本。
                    </div>
                  </div>

                  {/* Stage 053B Dynamic Observational Metrics */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                    <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>描述性滚动胜率</div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: modalBacktest?.status === 'fresh' ? 'var(--color-bullish)' : 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                        {modalBacktest?.status === 'fresh' && modalBacktest?.metrics ? formatDecimalPercent(modalBacktest.metrics.win_rate_descriptive) : '—'}
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        已结算 {modalBacktest?.sample_count !== undefined ? modalBacktest.sample_count : '—'} / {modalBacktest?.required_samples !== undefined ? modalBacktest.required_samples : '—'} 样本
                      </div>
                    </div>

                    <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>获利因子 (Profit Factor)</div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: modalBacktest?.status === 'fresh' ? 'var(--accent-cyan)' : 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                        {modalBacktest?.status === 'fresh' && modalBacktest?.metrics ? (modalBacktest.metrics.gross_profit_factor ?? 'N/A (零亏损)') : '—'}
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        毛利总计 / |毛损总计|
                      </div>
                    </div>

                    <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>平均净利 (扣费后)</div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: modalBacktest?.status === 'fresh' ? 'var(--text-primary)' : 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                        {modalBacktest?.status === 'fresh' && modalBacktest?.metrics ? `${modalBacktest.metrics.net_return_average_bps} bps` : '—'}
                      </div>
                      <div style={{ fontSize: '10px', color: modalBacktest?.status === 'fresh' ? 'var(--color-bullish)' : 'var(--text-muted)', marginTop: '2px' }}>
                        扣除 14 bps 交易摩擦
                      </div>
                    </div>

                    <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>信号覆盖率</div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--accent-cyan)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                        {modalBacktest?.coverage_ratio ? formatDecimalPercent(modalBacktest.coverage_ratio) : '—'}
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        有效结算样本 / 触发信号
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: Volume Profile Distribution */}
              {modalTab === 'profile' && (
                <>
                  {/* Top Key Levels Card */}
                  {modalProfile?.data ? (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '12px',
                      }}
                    >
                      <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-surface)', border: '1px solid var(--badge-info-border)', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>VAH 价值区上限</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                          {formatFinancialPrice(modalProfile.data.vah_price, modalSymbol)}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          距现价 {modalProfile.data.distance_to_vah_pct}%
                        </div>
                      </div>

                      <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-surface)', border: '1px solid var(--badge-bearish-border)', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: 'var(--color-rose)', fontWeight: 700 }}>★ POC 核心吸筹密集位</div>
                        <div style={{ fontSize: '17px', fontWeight: 800, color: 'var(--color-rose)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                          {formatFinancialPrice(modalProfile.data.poc_price, modalSymbol)}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--color-rose)', marginTop: '2px' }}>
                          距现价 {modalProfile.data.distance_to_poc_pct}%
                        </div>
                      </div>

                      <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-surface)', border: '1px solid var(--badge-info-border)', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>VAL 价值区下限</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                          {formatFinancialPrice(modalProfile.data.val_price, modalSymbol)}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          距现价 {modalProfile.data.distance_to_val_pct}%
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Horizontal Histogram Chart */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
                        价量分布横向柱状图 (Volume-at-Price Distribution)
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        🔴 POC 核心吸筹位 · 🔵 70% 价值区 (Value Area)
                      </div>
                    </div>

                    <div
                      style={{
                        maxHeight: '340px',
                        overflowY: 'auto',
                        borderRadius: '8px',
                        background: 'var(--bg-canvas)',
                        border: '1px solid var(--border-subtle)',
                        padding: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      {modalProfile?.distribution && modalProfile.distribution.length > 0 ? (
                        modalProfile.distribution.map((bin) => {
                          const pctVal = bin.volume_pct || '0';
                          return (
                            <div
                              key={bin.bin_index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                fontSize: '11px',
                                fontFamily: 'var(--font-mono)',
                                background: bin.is_poc
                                  ? 'var(--badge-bearish-bg)'
                                  : bin.is_inside_va
                                  ? 'var(--badge-info-bg)'
                                  : 'transparent',
                                padding: '3px 8px',
                                borderRadius: '4px',
                              }}
                            >
                              <span style={{ width: '85px', color: 'var(--text-secondary)' }}>
                                {formatFinancialPrice(bin.price_center, modalSymbol)}
                              </span>
                              <div style={{ flex: 1, height: '14px', background: 'var(--bg-surface)', borderRadius: '3px', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${pctVal}%`,
                                    minWidth: '4px',
                                    height: '100%',
                                    background: bin.is_poc
                                      ? 'var(--color-rose)'
                                      : bin.is_inside_va
                                      ? 'var(--accent-cyan)'
                                      : 'var(--border-subtle)',
                                    borderRadius: '3px',
                                    transition: 'width 0.2s ease',
                                  }}
                                />
                              </div>
                              <span style={{ width: '55px', textAlign: 'right', color: 'var(--text-muted)', fontSize: '11px' }}>
                                {pctVal}%
                              </span>
                              <span style={{ width: '65px', textAlign: 'right', fontWeight: 700, fontSize: '10px', color: bin.is_poc ? 'var(--color-rose)' : 'var(--accent-cyan)' }}>
                                {bin.is_poc ? '★ POC' : bin.is_inside_va ? 'VA 70%' : ''}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '160px', color: 'var(--text-muted)', fontSize: '12px', gap: '6px' }}>
                          <span style={{ fontSize: '24px' }}>📡</span>
                          <span>{modalProfile?.status === 'loading' ? '正在计算价量分布数据...' : '后端时序数据流因果闭合中，暂无充足价量分布'}</span>
                          <span style={{ fontSize: '11px', opacity: 0.7 }}>当前显示系统基准行情与最新态势标签</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              {/* TAB 3: Tag Attribution Lab (Stage 067) */}
              {modalTab === 'attribution' && (
                <TagAttributionLabTab
                  initialSymbol={modalSymbol}
                  initialTimeframe={selectedTimeframe}
                  compactMode
                />
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-subtle)', padding: '14px 24px', background: 'var(--bg-surface)' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                Market-Hub Data Center · 纯只读数据分析
              </div>
              <button
                onClick={() => setModalSymbol(null)}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: 'var(--btn-primary-bg)',
                  color: 'var(--btn-primary-text)',
                  border: 'none',
                  transition: 'opacity 0.15s ease',
                }}
              >
                关闭详情
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
